import { Select, Input, InputNumber, DatePicker, message } from "antd";
import TextArea from "antd/lib/input/TextArea";
import { useContext, useEffect, useState } from "react";
import { GlobalStateContext } from "../../../utils/GlobalStateContext";
import {
  newClientEscalation,
  buildSortedOptions,
} from "../../../utils/Constants";
import { useDispatch } from "react-redux";
import secureLocalStorage from "react-secure-storage";
import dayjs from "dayjs";

import { fetchUsersThunk } from "../../../store/thunks/usersThunk";

import { fetchProcessesByClientThunk } from "../../../store/thunks/processesThunk";

import { fetchClientsByDeptThunk } from "../../../store/thunks/clientsThunk";

import { fetchDepartmentsSimpleThunk } from "../../../store/thunks/departmentsThunk";

import { fetchDashboardUsersThunk } from "../../../store/thunks/dashboardThunk";

import "./newClientEscalation.css";

const onSearch = () => {};

const EditClientEscalation = ({ editingRecord }) => {
  const dispatch = useDispatch();

  const { globalState, setGlobalState } = useContext(GlobalStateContext);

  const [departments, setDepartments] = useState([]);
  const [clients, setClients] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [reportingTLs, setReportingTLs] = useState([]);

  const slsRole = secureLocalStorage.getItem("role");
  const slsLIUId = secureLocalStorage.getItem("lIUId");

  /*
   * ============================================================
   * HELPERS
   * ============================================================
   */

  const getName = (value) => {
    if (!value) return "";

    if (typeof value === "object") {
      return value?.name || "";
    }

    return value;
  };

  const getId = (value) => {
    if (!value) return null;

    if (typeof value === "object") {
      return value?._id || null;
    }

    return value;
  };

  const isValidObjectId = (value) => {
    if (!value || typeof value !== "string") {
      return false;
    }

    return /^[a-f\d]{24}$/i.test(value);
  };

  const findOptionByName = (options, value) => {
    if (!value || !Array.isArray(options)) {
      return null;
    }

    const name = getName(value)?.trim().toLowerCase();

    return (
      options.find((option) => option?.label?.trim().toLowerCase() === name) ||
      null
    );
  };

  /*
   * ============================================================
   * FETCH DEPARTMENTS
   * ============================================================
   */

  const fetchDepartments = async () => {
    try {
      const response = await dispatch(
        fetchDepartmentsSimpleThunk({
          route: "departments",
          action: "findAll",
        }),
      ).unwrap();

      const options =
        response?.map((department) => ({
          label: department?.name,
          value: department?._id,
        })) || [];

      setDepartments(options);

      return options;
    } catch (error) {
      console.error("Error fetching departments:", error);

      setDepartments([]);

      return [];
    }
  };

  /*
   * ============================================================
   * FETCH CLIENTS BY DEPARTMENT
   * ============================================================
   */

  const fetchClientsByDepartment = async (departmentId) => {
    if (!departmentId || !isValidObjectId(departmentId)) {
      console.error("Invalid department ID:", departmentId);

      setClients([]);

      return [];
    }

    try {
      const response = await dispatch(
        fetchClientsByDeptThunk({
          department: departmentId,
        }),
      ).unwrap();

      const options =
        response?.map((client) => ({
          label: client?.name,
          value: client?._id,
        })) || [];

      setClients(options);

      return options;
    } catch (error) {
      console.error("Error fetching clients:", error);

      setClients([]);

      return [];
    }
  };

  /*
   * ============================================================
   * FETCH PROCESSES BY CLIENT + DEPARTMENT
   * ============================================================
   */

  const fetchProcessesByClient = async (clientId, departmentId) => {
    if (
      !clientId ||
      !departmentId ||
      !isValidObjectId(clientId) ||
      !isValidObjectId(departmentId)
    ) {
      setProcesses([]);

      return [];
    }

    try {
      const response = await dispatch(
        fetchProcessesByClientThunk({
          client: clientId,
          department: departmentId,
        }),
      ).unwrap();

      const options =
        response?.map((process) => ({
          label: process?.name,
          value: process?._id,
        })) || [];

      setProcesses(options);

      return options;
    } catch (error) {
      console.error("Error fetching processes:", error);

      setProcesses([]);

      return [];
    }
  };

  /*
   * ============================================================
   * FETCH REPORTING TL BY DEPARTMENT
   * ============================================================
   */

  const fetchReportingTLByDepartment = async (departmentId) => {
    if (!departmentId || !isValidObjectId(departmentId)) {
      console.error("Invalid Reporting TL department ID:", departmentId);

      setReportingTLs([]);

      return [];
    }

    try {
      const payload = {
        route: "users",
        action: "findAll",
        fields: ["tl", "atl"],
        role: slsRole,
        lIUId: slsLIUId,
        departments: [departmentId],
      };

      const result = await dispatch(fetchDashboardUsersThunk(payload)).unwrap();

      const users = Array.isArray(result) ? result : [];

      const tlUsers = users.filter(
        (user) =>
          user?.role === process.env.REACT_APP_TLROLE ||
          user?.role === process.env.REACT_APP_ATLROLE,
      );

      const options = buildSortedOptions(tlUsers, "name", "_id", true, [
        process.env.REACT_APP_TLROLE?.toLowerCase(),
        process.env.REACT_APP_ATLROLE?.toLowerCase(),
      ]);

      setReportingTLs(options);

      return options;
    } catch (error) {
      console.error("Error fetching Reporting TL:", error);

      setReportingTLs([]);

      return [];
    }
  };

  /*
   * ============================================================
   * GET AGENT / AUDITOR OPTIONS
   *
   * We keep this separate because those options are normally
   * already available from the field configuration / API.
   * ============================================================
   */

  const getFieldOptions = (field, currentOptions) => {
    if (field.key === "eDepartmentName") {
      return departments;
    }

    if (field.key === "eClientName") {
      return clients;
    }

    if (field.key === "eProcessName") {
      return processes;
    }

    if (field.key === "eReportingTlName") {
      return reportingTLs;
    }

    return currentOptions || [];
  };

  /*
   * ============================================================
   * INITIAL EDIT LOAD
   *
   * IMPORTANT:
   *
   * 1. Load departments
   * 2. Find department ID
   * 3. Load clients
   * 4. Find client ID
   * 5. Load processes
   * 6. Find process ID
   * 7. Load Reporting TL
   * 8. Find Reporting TL ID
   * 9. Populate complete globalState
   * ============================================================
   */

  /*
   * ============================================================
   * SELECT CHANGE
   * ============================================================
   */

  const handleSelectChange = (field, value, option) => {
    setGlobalState((prev) => ({
      ...prev,

      [`${field.key}Val`]: option?.label || null,

      [field.key]: value || field.errorMessage,

      /*
       * Department changed
       */
      ...(field.key === "eDepartmentName" && {
        eClientName: null,
        eClientNameVal: null,

        eProcessName: null,
        eProcessNameVal: null,

        eReportingTlName: null,
        eReportingTlNameVal: null,
      }),

      /*
       * Client changed
       */
      ...(field.key === "eClientName" && {
        eProcessName: null,
        eProcessNameVal: null,
      }),
    }));

    /*
     * Department
     */
    if (field.key === "eDepartmentName") {
      fetchClientsByDepartment(value);

      fetchReportingTLByDepartment(value);
    }

    /*
     * Client
     */
    if (field.key === "eClientName") {
      fetchProcessesByClient(value, globalState?.eDepartmentName);
    }
  };

  const fetchAgentByEmpId = async (empId) => {
  if (!empId) {
    setGlobalState((prev) => ({
      ...prev,
      eAgentName: null,
      eAgentNameVal: null,
    }));

    return;
  }

  try {
    const response = await dispatch(
      fetchUsersThunk({
        searchIn: "users",
        callActionFrom: "client-escalation",
        hasSearchedValue: "yes",
        searchKeyword: String(empId),
        pagination: {
          current: 1,
          page: 1,
          pageSize: 10,
        },
      }),
    ).unwrap();

    const users = response?.data || [];

    const agent = users.find(
      (user) =>
        String(user?.empID) === String(empId) &&
        user?.role?.toLowerCase() !==
          process.env.REACT_APP_AROLE?.toLowerCase(),
    );

    if (agent) {
      setGlobalState((prev) => ({
        ...prev,
        eAgentName: agent?.name,
        eAgentNameVal: agent?.name,
      }));
    } else {
      setGlobalState((prev) => ({
        ...prev,
        eAgentName: null,
        eAgentNameVal: null,
      }));

      message.error("Agent not found for this Emp ID.");
    }
  } catch (error) {
    console.error(
      "Error fetching agent by Emp ID:",
      error,
    );

    setGlobalState((prev) => ({
      ...prev,
      eAgentName: null,
      eAgentNameVal: null,
    }));

    message.error("Unable to fetch Agent details.");
  }
};

  useEffect(() => {
    if (!editingRecord) return;

    const loadEditData = async () => {
      try {
        // =====================================================
        // 1. LOAD DEPARTMENTS
        // =====================================================

        const departmentOptions = await fetchDepartments();

        const departmentOption = findOptionByName(
          departmentOptions,
          editingRecord.department,
        );

        const departmentId = departmentOption?.value;

        if (!departmentId) {
          console.error("Department not found:", editingRecord.department);
          return;
        }

        // =====================================================
        // 2. LOAD CLIENTS + REPORTING TL
        // =====================================================

        const clientOptions = await fetchClientsByDepartment(departmentId);

        const reportingTlOptions =
          await fetchReportingTLByDepartment(departmentId);

        // =====================================================
        // 3. FIND CLIENT
        // =====================================================

        const clientOption = findOptionByName(
          clientOptions,
          editingRecord.client,
        );

        const clientId = clientOption?.value;

        // =====================================================
        // 4. LOAD PROCESSES
        // =====================================================

        let processOptions = [];

        if (clientId) {
          processOptions = await fetchProcessesByClient(clientId, departmentId);
        }

        // =====================================================
        // 5. FIND PROCESS
        // =====================================================

        const processOption = findOptionByName(
          processOptions,
          editingRecord.process,
        );

        // =====================================================
        // 6. FIND REPORTING TL
        // =====================================================

        const reportingTlOption = findOptionByName(
          reportingTlOptions,
          editingRecord.reportingTl,
        );

        // =====================================================
        // 7. PREPARE ERROR RAISE DATE
        // =====================================================
        // IMPORTANT:
        // Use the RAW ISO value, not the formatted
        // "DD/MM/YYYY H:mm A" table value.

        const errorRaiseDate = editingRecord?.errorRaiseDateRaw
          ? dayjs(editingRecord.errorRaiseDateRaw)
          : null;

        // =====================================================
        // 8. PREPARE OTHER DATE VALUES
        // =====================================================

        const transactionDate = editingRecord?.transactionDate
          ? dayjs(editingRecord.transactionDate)
          : null;

        const feedbackDate = editingRecord?.feedbackdate
          ? dayjs(editingRecord.feedbackdate)
          : null;

        // =====================================================
        // 9. POPULATE GLOBAL STATE
        // =====================================================

        setGlobalState((prev) => ({
          ...prev,

          // ===================================================
          // EDIT ID
          // ===================================================

          clientEscalationEditId:
            editingRecord?.id || editingRecord?._id || null,

          // ===================================================
          // DEPARTMENT
          // ===================================================

          eDepartmentName: departmentOption?.value || null,

          eDepartmentNameVal:
            departmentOption?.label || editingRecord?.department || null,

          // ===================================================
          // CLIENT
          // ===================================================

          eClientName: clientOption?.value || null,

          eClientNameVal: clientOption?.label || editingRecord?.client || null,

          // ===================================================
          // PROCESS
          // ===================================================

          eProcessName: processOption?.value || null,

          eProcessNameVal: processOption?.label || editingRecord?.value || null,

          // ===================================================
          // REPORTING TL
          // ===================================================

          eReportingTlName: reportingTlOption?.value || null,

          eReportingTlNameVal:
            reportingTlOption?.label || editingRecord?.reportingTl || null,

          // ===================================================
          // CLIENT SPOC EMAIL
          // ===================================================

          eClientSpocEmail: editingRecord?.clientSpocEmail || null,

          eClientSpocEmailVal: editingRecord?.clientSpocEmail || null,

          // ===================================================
          // EMP ID
          // ===================================================

          eEmpID: editingRecord?.empID || null,

          eEmpIDVal: editingRecord?.empID || null,

          // ===================================================
          // FINANCIAL IMPACT
          // ===================================================

          eFinancialImpact: editingRecord?.financialImpact || null,

          eFinancialImpactVal: editingRecord?.financialImpact || null,

          // ===================================================
          // AGENT
          // ===================================================

          eAgentName:
            editingRecord?.agent?.name || editingRecord?.agent || null,

          eAgentNameVal:
            editingRecord?.agent?.name || editingRecord?.agent || null,

          // ===================================================
          // AUDITOR
          // ===================================================

          eAuditorName:
            editingRecord?.auditor?.name || editingRecord?.auditor || null,

          eAuditorNameVal:
            editingRecord?.auditor?.name || editingRecord?.auditor || null,

          // ===================================================
          // TRANSACTION ID
          // ===================================================
          // IMPORTANT:
          // handleOk uses eTransactionId, not eTransaction.

          eTransactionId:
            editingRecord?.transactionId || editingRecord?.transaction || null,

          eTransactionIdVal:
            editingRecord?.transactionId || editingRecord?.transaction || null,

          // Keep these only if other existing code still uses them.
          eTransaction:
            editingRecord?.transactionId || editingRecord?.transaction || null,

          eTransactionVal:
            editingRecord?.transactionId || editingRecord?.transaction || null,

          // ===================================================
          // TRANSACTION DATE
          // ===================================================

          eTransactionDate: transactionDate,

          eTransactionDateVal: transactionDate,

          // ===================================================
          // ERROR RAISE DATE
          // ===================================================
          // IMPORTANT:
          // This must be the Dayjs object created from
          // errorRaiseDateRaw.

          eErrorRaiseDate: errorRaiseDate,

          eErrorRaiseDateVal: errorRaiseDate,

          // ===================================================
          // ERROR DESCRIPTION
          // ===================================================

          eErrorDescription: editingRecord?.errorDescription || null,

          eErrorDescriptionVal: editingRecord?.errorDescription || null,

          // ===================================================
          // ERROR IMPACT
          // ===================================================

          eErrorImpact: editingRecord?.errorImpact || null,

          eErrorImpactVal: editingRecord?.errorImpact || null,

          // ===================================================
          // ERROR AREA
          // ===================================================

          eErrorArea: editingRecord?.errorArea || null,

          eErrorAreaVal: editingRecord?.errorArea || null,

          // ===================================================
          // ERROR TYPE
          // ===================================================

          eErrorType: editingRecord?.errorType || null,

          eErrorTypeVal: editingRecord?.errorType || null,

          // ===================================================
          // ERROR STATUS
          // ===================================================

          eErrorStatus: editingRecord?.errorStatus || null,

          eErrorStatusVal: editingRecord?.errorStatus || null,

          // ===================================================
          // DAY OF WEEK
          // ===================================================

          eDayoftheweek: editingRecord?.dayoftheweek || null,

          eDayoftheweekVal: editingRecord?.dayoftheweek || null,

          // ===================================================
          // AGENT TENURE
          // ===================================================

          eAgentTenure: editingRecord?.agentTenure || null,

          eAgentTenureVal: editingRecord?.agentTenure || null,

          // ===================================================
          // FEEDBACK DATE
          // ===================================================

          eFeedbackdate: feedbackDate,

          eFeedbackdateVal: feedbackDate,

          // ===================================================
          // REPEATED ERROR
          // ===================================================

          eRepeatedError: editingRecord?.repeatedError || null,

          eRepeatedErrorVal: editingRecord?.repeatedError || null,

          // ===================================================
          // CORRECTIVE ACTION
          // ===================================================

          eCorrectiveActionTaken: editingRecord?.correctiveActionTaken || null,

          eCorrectiveActionTakenVal:
            editingRecord?.correctiveActionTaken || null,

          // ===================================================
          // RCA LEVEL 1
          // ===================================================

          rcaLevelOne: editingRecord?.rcaLevelOne || null,

          rcaLevelOneVal: editingRecord?.rcaLevelOne || null,

          // ===================================================
          // RCA LEVEL 2
          // ===================================================

          rcaLevelTwo: editingRecord?.rcaLevelTwo || null,

          rcaLevelTwoVal: editingRecord?.rcaLevelTwo || null,

          // ===================================================
          // RCA LEVEL 3
          // ===================================================

          rcaLevelThree: editingRecord?.rcaLevelThree || null,

          rcaLevelThreeVal: editingRecord?.rcaLevelThree || null,

          // ===================================================
          // RCA LEVEL 4
          // ===================================================

          rcaLevelFour: editingRecord?.rcaLevelFour || null,

          rcaLevelFourVal: editingRecord?.rcaLevelFour || null,
        }));
      } catch (error) {
        console.error("Error loading edit data:", error);
      }
    };

    loadEditData();
  }, [editingRecord]);

  /*
   * ============================================================
   * RENDER
   * ============================================================
   */

  return (
    <div id="antdformstyle">
      {newClientEscalation.map((section) => (
        <div key={section.key}>
          <div className="pb-16 cusFormTitle">{section.title}</div>

          <div className="details-container plr-16">
            {section.fields
              .reduce((rows, field, index) => {
                if (index % 2 === 0) {
                  rows.push(section.fields.slice(index, index + 2));
                }

                return rows;
              }, [])
              .map((fieldPair, pairIndex) => (
                <div className="details-wrap" key={pairIndex}>
                  {fieldPair.map((field) => (
                    <div key={field.key} className="details-inner">
                      <label className="formCusLabel">
                        {field.label}

                        {field.required && <span className="reqred">*</span>}
                      </label>

                      {/* ================================================= */}
                      {/* INPUT */}
                      {/* ================================================= */}

                      {field.type === "input" && (
                        <Input
                          placeholder={field.placeholder}
                          value={globalState?.[`${field.key}Val`] ?? ""}
                          onChange={(e) =>
                            setGlobalState((prev) => ({
                              ...prev,

                              [`${field.key}Val`]: e.target.value,

                              [field.key]: e.target.value || field.errorMessage,
                            }))
                          }
                        />
                      )}

                      {/* ================================================= */}
                      {/* NUMBER */}
                      {/* ================================================= */}

                      {field.type === "number" && (
                        <InputNumber
                          placeholder={field.placeholder}
                          value={globalState?.[`${field.key}Val`]}
                          onChange={(value) => {
                            setGlobalState((prev) => ({
                              ...prev,

                              [`${field.key}Val`]: value,

                              [field.key]:
                                value || field.errorMessage,
                            }));

                            if (
                              field.key === "eEmpID" &&
                              String(value || "").length >= 3
                            ) {
                              fetchAgentByEmpId(value);
                            }
                          }}
                        />
                      )}

                      {/* ================================================= */}
                      {/* READONLY */}
                      {/* ================================================= */}

                      {field.type === "readonly" && (
                        <div className="readonly-field">
                          <span>{globalState?.[`${field.key}Val`]}</span>
                        </div>
                      )}

                      {/* ================================================= */}
                      {/* DATE */}
                      {/* ================================================= */}

                      {field.type === "date" && (
                        <DatePicker
                          style={{
                            width: "100%",
                          }}
                          placeholder={field.placeholder}
                          value={
                            globalState?.[`${field.key}Val`] &&
                            dayjs(globalState?.[`${field.key}Val`]).isValid()
                              ? dayjs(globalState?.[`${field.key}Val`])
                              : null
                          }
                          disabledDate={(current) => {
                            /*
                             * Transaction Date
                             * cannot be after
                             * Error Raise Date
                             */
                            if (
                              field.key === "eTransactionDate" &&
                              globalState?.eErrorRaiseDateVal
                            ) {
                              return current.isAfter(
                                dayjs(globalState.eErrorRaiseDateVal),
                                "day",
                              );
                            }

                            return false;
                          }}
                          onChange={(date) => {
                            const dayName = date ? date.format("ddd") : "";

                            setGlobalState((prev) => ({
                              ...prev,

                              [`${field.key}Val`]: date,

                              [field.key]: date || field.errorMessage,

                              ...(field.key === "eTransactionDate" && {
                                eDayoftheweekVal: dayName || null,

                                eDayoftheweek: dayName || null,
                              }),
                            }));
                          }}
                        />
                      )}

                      {/* ================================================= */}
                      {/* TEXTAREA */}
                      {/* ================================================= */}

                      {field.type === "textarea" && (
                        <TextArea
                          placeholder={field.placeholder}
                          value={globalState?.[`${field.key}Val`] ?? ""}
                          onChange={(e) =>
                            setGlobalState((prev) => ({
                              ...prev,

                              [`${field.key}Val`]: e.target.value,

                              [field.key]: e.target.value || field.errorMessage,
                            }))
                          }
                        />
                      )}

                      {/* ================================================= */}
                      {/* SELECT */}
                      {/* ================================================= */}

                      {(field.type === "select" ||
                        [
                          "eDepartmentName",
                          "eClientName",
                          "eProcessName",
                          "eReportingTlName",
                        ].includes(field.key)) && (
                        <Select
                          showSearch
                          allowClear
                          placeholder={field.placeholder}
                          optionFilterProp="label"
                          value={globalState?.[field.key] || null}
                          onChange={(value, option) =>
                            handleSelectChange(field, value, option)
                          }
                          filterOption={(input, option) =>
                            (option?.label || "")
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                          options={
                            field.key === "eDepartmentName"
                              ? departments
                              : field.key === "eClientName"
                                ? clients
                                : field.key === "eProcessName"
                                  ? processes
                                  : field.key === "eReportingTlName"
                                    ? reportingTLs
                                    : field.options
                          }
                          disabled={
                            field.key === "eClientName"
                              ? !globalState?.eDepartmentName
                              : field.key === "eProcessName"
                                ? !globalState?.eClientName
                                : field.key === "eReportingTlName"
                                  ? !globalState?.eDepartmentName
                                  : false
                          }
                        />
                      )}

                      {/* ================================================= */}
                      {/* VALIDATION ERROR */}
                      {/* ================================================= */}

                      {globalState?.[field.key] === field.errorMessage && (
                        <div className="error">{field.errorMessage}</div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default EditClientEscalation;
